import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';

// Hook: useEmployeeAssignments(employeeId)
// Returns { flattened, nested, loading, error, saveAssignments }
// - flattened: array of flattened assignment docs ({ id, ...data })
// - nested: legacy nested map { [locationId]: { departments: { [deptId]: { roles: { [roleId]: { assigned, isPrimary, wage, appearsOnSchedule } } } } } }
// - saveAssignments(pendingNested): accepts legacy nested map and writes flattened docs atomically

export default function useEmployeeAssignments(employeeId) {
  const [flattened, setFlattened] = useState([]);
  const [nested, setNested] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!employeeId) {
      setFlattened([]);
      setNested({});
      setLoading(false);
      return;
    }

    let unsub = null;
    let mounted = true;

    (async () => {
      try {
        const tenantId = await tryResolveTenantId();
        if (!tenantId) {
          if (!mounted) return;
          setFlattened([]);
          setNested({});
          setLoading(false);
          return;
        }

        const colRef = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');
        const q = query(colRef, orderBy('locationId'));

        unsub = onSnapshot(q, snap => {
          if (!mounted) return;
          const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
          setFlattened(docs);

          // Build nested map for compatibility with existing components
          const nestedMap = {};
          docs.forEach(d => {
            const locId = d.locationId;
            const deptId = d.departmentId || 'unknown_department';
            const roleId = d.roleId;
            if (!locId || !roleId) return; // skip malformed docs

            if (!nestedMap[locId]) nestedMap[locId] = { departments: {} };
            if (!nestedMap[locId].departments[deptId]) nestedMap[locId].departments[deptId] = { roles: {} };

            nestedMap[locId].departments[deptId].roles[roleId] = {
              assigned: typeof d.assigned === 'boolean' ? d.assigned : true,
              isPrimary: !!d.isPrimary,
              appearsOnSchedule: typeof d.appearsOnSchedule === 'boolean' ? d.appearsOnSchedule : true,
              wage: typeof d.wage !== 'undefined' ? d.wage : null
            };
          });

          setNested(nestedMap);
          setLoading(false);
          setError(null);
        }, err => {
          console.warn('assignments listener error', err);
          if (!mounted) return;
          setError(err);
          setLoading(false);
        });
      } catch (err) {
        console.error('useEmployeeAssignments error', err);
        if (!mounted) return;
        setError(err);
        setLoading(false);
      }
    })();

    return () => { mounted = false; try { if (typeof unsub === 'function') unsub(); } catch (e) {} };
  }, [employeeId]);

  // saveAssignments: accepts legacy nested map and writes flattened docs atomically
  const saveAssignments = useCallback(async (pendingNested) => {
    if (!employeeId) throw new Error('employeeId required');
    const tenantId = await tryResolveTenantId();
    if (!tenantId) throw new Error('tenant not resolved');

    const assignmentsCol = collection(db, `tenants/${tenantId}/employees`, employeeId, 'assignments');

    // Build target map keyed by `${locationId}_${roleId}`
    const target = {};
    if (pendingNested) {
      Object.entries(pendingNested).forEach(([locId, locObj]) => {
        const depts = (locObj && locObj.departments) || {};
        Object.entries(depts).forEach(([deptId, deptObj]) => {
          const roles = (deptObj && deptObj.roles) || {};
          // Support legacy array format
          if (Array.isArray(roles)) {
            roles.forEach(rid => {
              const id = `${locId}_${rid}`;
              target[id] = {
                employeeId,
                locationId: locId,
                departmentId: deptId,
                roleId: rid,
                assigned: true,
                isPrimary: false,
                appearsOnSchedule: true,
                updatedAt: serverTimestamp()
              };
            });
          } else {
            Object.entries(roles).forEach(([rid, rmeta]) => {
              const id = `${locId}_${rid}`;
              target[id] = {
                employeeId,
                locationId: locId,
                departmentId: deptId,
                roleId: rid,
                assigned: typeof rmeta.assigned === 'boolean' ? rmeta.assigned : true,
                isPrimary: !!rmeta.isPrimary,
                appearsOnSchedule: typeof rmeta.appearsOnSchedule === 'boolean' ? rmeta.appearsOnSchedule : true,
                wage: typeof rmeta.wage !== 'undefined' ? rmeta.wage : null,
                updatedAt: serverTimestamp()
              };
            });
          }
        });
      });
    }

    // Read existing flattened docs so we can delete removed ones and preserve createdAt
    const existingSnap = await getDocs(assignmentsCol);
    const existingMap = {};
    existingSnap.docs.forEach(d => existingMap[d.id] = d.data());

    const batch = writeBatch(db);

    // Upsert target docs
    for (const [docId, payload] of Object.entries(target)) {
      const ref = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', docId);
      // Preserve createdAt if existed
      if (existingMap[docId] && existingMap[docId].createdAt) {
        payload.createdAt = existingMap[docId].createdAt;
      } else {
        payload.createdAt = serverTimestamp();
      }
      // updatedAt already set to serverTimestamp()
      batch.set(ref, payload, { merge: true });
    }

    // Delete docs that exist but are not in target
    Object.keys(existingMap).forEach(id => {
      if (!target[id]) {
        const ref = doc(db, `tenants/${tenantId}/employees`, employeeId, 'assignments', id);
        batch.delete(ref);
      }
    });

    await batch.commit();
  }, [employeeId]);

  return { flattened, nested, loading, error, saveAssignments };
}
